import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { transcriptionApi } from '../utils/api';
import type { User, DownloadFormat, TranscriptSegment } from '../types';
import { useTranscription } from '../hooks/queries/useTranscription';
import { formatDuration, formatTimestamp, transcriptionTitle } from '../utils/formatters';
import { getSpeakerDisplayMap, listDetectedSpeakers, getEffectiveSegments, splitSegment, type SpeakerDisplay } from '../utils/speakerColor';
import { useSegmentPlayback } from '../hooks/useSegmentPlayback';
import { Spinner, ErrorState, Card, Button, Badge } from '../components/ui';
import { statusToBadgeVariant, statusToLabel } from '../utils/statusBadge';
import { linkButtonClass } from '../utils/linkButtonClass';
import { DOWNLOADS_ENABLED } from '../config/featureFlags';
import TranscriptionProgress from '../components/TranscriptionProgress';
import DownloadMenu from '../components/DownloadMenu';
import ConfirmDialog from '../components/ConfirmDialog';
import Layout from '../components/Layout';
import Header from '../components/Header';

// セグメント編集のドラフト1件。id は React の key（分割で並びが変わっても textarea を取り違えない）
type DraftSegment = TranscriptSegment & { id: number };

interface TranscriptionPageProps {
  user: User;
}

// 編集用 textarea の高さを内容に合わせる（枠線の分を足してスクロールバーを出さない）
const autoGrow = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
};

export default function TranscriptionPage({ user }: TranscriptionPageProps) {
  const { id } = useParams<{ id: string }>();
  // 取得＋ポーリング（status 依存の refetchInterval）は Hook が担う
  const { data: transcription, isLoading, isError, error, refetch } = useTranscription(id);

  // 話者名一括リネーム（Unit 005）。standard / unlimited のみ操作可能
  const canRename = user.plan.plan_type === 'standard' || user.plan.plan_type === 'unlimited';
  // セグメント音声再生（Unit 006 / 008）。再生可否の真実源は audio_available（サーバ側保持ゲート＝保持キー＋未期限・所有権）。
  // この変数は「再生可否」ではなく、現 unlimited 向けに期限切れ/partial の説明文（案内ブロック）を出すための案内ゲート用途。
  // 実際の再生（ボタンの表示・活性）は audio_available のみが決め、プランに依存しない（降格後も保持期限内は再生可）。
  const canPlaySegments = user.plan.plan_type === 'unlimited';
  const { playingKey, play, stop } = useSegmentPlayback(transcription?.audio_url);

  // 表示タイトルのリネーム。アップロード時のファイル名は機械的で見出しに向かないため、
  // ユーザーが分かりやすい名前を付けられる。空にすると未設定へ戻り、表示はファイル名に戻る。
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [saveTitleError, setSaveTitleError] = useState<string | null>(null);

  // 破棄確認の対象。未保存の変更があるまま「キャンセル」を押したときにだけ確認を挟む
  const [pendingDiscard, setPendingDiscard] = useState<null | 'title' | 'names' | 'segments'>(null);

  const startEditingTitle = () => {
    setDraftTitle(transcription?.title ?? '');
    setSaveTitleError(null);
    setIsEditingTitle(true);
  };

  const saveTitle = async () => {
    if (!id) return;
    setIsSavingTitle(true);
    setSaveTitleError(null);
    try {
      // 空欄は null（未設定）で送る＝表示はファイル名へフォールバック
      await transcriptionApi.updateTitle(id, draftTitle.trim() || null);
      await refetch();
      setIsEditingTitle(false);
    } catch (err) {
      console.error('Failed to update title:', err);
      setSaveTitleError('タイトルの保存に失敗しました。もう一度お試しください。');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const discardEditingTitle = () => {
    setIsEditingTitle(false);
    setSaveTitleError(null);
  };

  const cancelEditingTitle = () => {
    if (draftTitle.trim() !== (transcription?.title ?? '').trim()) {
      setPendingDiscard('title');
      return;
    }
    discardEditingTitle();
  };

  // 破棄確認で「破棄する」を選んだときの実行先
  const confirmDiscard = () => {
    if (pendingDiscard === 'title') discardEditingTitle();
    if (pendingDiscard === 'names') discardEditingNames();
    if (pendingDiscard === 'segments') discardEditingSegments();
    setPendingDiscard(null);
  };

  const [isEditingNames, setIsEditingNames] = useState(false);
  const [draftNames, setDraftNames] = useState<Record<string, string>>({});
  const [isSavingNames, setIsSavingNames] = useState(false);
  const [saveNamesError, setSaveNamesError] = useState<string | null>(null);

  const startEditingNames = (detected: SpeakerDisplay[]) => {
    // 入力初期値: 既存の上書き名があればそれ、無ければ空欄（プレースホルダで話者N を示す）
    const initial: Record<string, string> = {};
    for (const sp of detected) {
      initial[sp.rawLabel] = transcription?.speaker_names?.[sp.rawLabel] ?? '';
    }
    setDraftNames(initial);
    setSaveNamesError(null);
    setIsEditingNames(true);
  };

  // 話者名に未保存の変更があるか。draftNames は編集開始時に保存済みの値で初期化されるため、
  // 各エントリを保存済みの値と突き合わせれば差分が判定できる。
  const isNamesDirty = () =>
    Object.entries(draftNames).some(
      ([rawLabel, name]) =>
        name.trim() !== (transcription?.speaker_names?.[rawLabel] ?? '').trim(),
    );

  const discardEditingNames = () => {
    setIsEditingNames(false);
    setSaveNamesError(null);
  };

  const cancelEditingNames = () => {
    // 未保存の変更がある場合だけ確認を挟む（変更していなければ即閉じる方が快適）
    if (isNamesDirty()) {
      setPendingDiscard('names');
      return;
    }
    discardEditingNames();
  };

  const saveSpeakerNames = async () => {
    if (!id) return;
    setIsSavingNames(true);
    setSaveNamesError(null);
    try {
      // 最終状態を全置換で送信（空欄はサーバ側で除外＝既定の話者N に戻る）
      await transcriptionApi.updateSpeakerNames(id, draftNames);
      // サーバを真実として再取得（失敗時は表示が変わらず不整合を残さない）
      await refetch();
      setIsEditingNames(false);
    } catch (err) {
      console.error('Failed to update speaker names:', err);
      setSaveNamesError('話者名の保存に失敗しました。もう一度お試しください。');
    } finally {
      setIsSavingNames(false);
    }
  };

  // セグメント単位の手動修正（Unit 007）。unlimited のみ操作可能
  const canEditSegments = user.plan.plan_type === 'unlimited';
  const [isEditingSegments, setIsEditingSegments] = useState(false);
  // 編集中のセグメント配列（v3.8.0: 分割で件数が変わるため index マップではなく配列で持つ）
  const [draftSegments, setDraftSegments] = useState<DraftSegment[]>([]);
  const nextDraftId = useRef(0);
  const textareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const [isSavingSegments, setIsSavingSegments] = useState(false);
  const [saveSegmentsError, setSaveSegmentsError] = useState<string | null>(null);

  // 書き起こし全文コピー（話者名＋発言をプレーンテキスト化）。押下直後にボタン表示を切り替えて完了を示す。
  const [copied, setCopied] = useState(false);
  const copyTranscript = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      // 一定時間で通常表示へ戻す（トースト基盤が無いためボタン内フィードバックで代替）
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy transcript:', err);
    }
  };

  const toDraft = (seg: TranscriptSegment): DraftSegment => ({ ...seg, id: nextDraftId.current++ });

  const startEditingSegments = () => {
    if (!transcription) return;
    // 表示モードと編集モードで行 id（再生キー）が変わるため、再生中の音声は止める
    stop();
    textareaRefs.current = {};
    // 現在の実効値（分割後の配列、または override 適用済みの元配列）を初期値に
    setDraftSegments(getEffectiveSegments(transcription).map(toDraft));
    setSaveSegmentsError(null);
    setIsEditingSegments(true);
  };

  const updateDraft = (id: number, patch: Partial<TranscriptSegment>) =>
    setDraftSegments((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  // カーソル位置で2つに分ける（v3.8.0）。両片に新しい id を振り、textarea を作り直して高さを合わせる
  const splitDraft = (id: number) => {
    const el = textareaRefs.current[id];
    const target = draftSegments.find((d) => d.id === id);
    const pieces = el && target ? splitSegment(target, el.selectionStart) : null;
    if (!pieces) {
      setSaveSegmentsError('分割したい位置にカーソルを置いてから押してください。');
      return;
    }
    setSaveSegmentsError(null);
    stop();
    delete textareaRefs.current[id];
    setDraftSegments((prev) => prev.flatMap((d) => (d.id === id ? pieces.map(toDraft) : [d])));
  };

  // 分割を含むか（含むなら編集後の配列で保存する。含まなければ従来の index 単位の上書きで保存する）
  const usesEditedSegments = () =>
    !!transcription?.edited_segments || draftSegments.length !== (transcription?.segments.length ?? 0);

  // ドラフトから「保存する最終状態」の override を構築する（分割なしのとき・Unit 007 と同一規則）。
  // 全置換セマンティクス:「差分」ではなく最終状態を送る。元値と同値・空テキストは含めない（＝元値にフォールバック）。
  const buildSegmentOverrides = () => {
    const speakerOverrides: Record<string, string> = {};
    const textOverrides: Record<string, string> = {};
    transcription?.segments.forEach((seg, i) => {
      const key = String(i);
      const sp = draftSegments[i]?.speaker;
      if (sp && sp !== seg.speaker) speakerOverrides[key] = sp;
      const tx = draftSegments[i]?.text;
      if (tx != null && tx.trim() && tx !== seg.text) textOverrides[key] = tx;
    });
    return { speakerOverrides, textOverrides };
  };

  // セグメントに未保存の変更があるか（開始時の実効値から件数・テキスト・話者が変わったか）
  const isSegmentsDirty = () => {
    if (!transcription) return false;
    const initial = getEffectiveSegments(transcription);
    return (
      initial.length !== draftSegments.length ||
      initial.some(
        (seg, i) => seg.text !== draftSegments[i].text || (seg.speaker ?? '') !== (draftSegments[i].speaker ?? ''),
      )
    );
  };

  const discardEditingSegments = () => {
    stop();
    setIsEditingSegments(false);
    setSaveSegmentsError(null);
  };

  const cancelEditingSegments = () => {
    // 丁寧に直したテキストが警告なしに消えるのを防ぐ（未変更なら確認は挟まない）
    if (isSegmentsDirty()) {
      setPendingDiscard('segments');
      return;
    }
    discardEditingSegments();
  };

  const saveSegmentEdits = async () => {
    if (!id || !transcription) return;
    setIsSavingSegments(true);
    setSaveSegmentsError(null);
    try {
      if (usesEditedSegments()) {
        // 分割片には戻す元テキストがないため、空のセグメントは送らない（サーバも 400 で弾く）
        if (draftSegments.some((d) => !d.text.trim())) {
          setSaveSegmentsError('空のセグメントがあります。テキストを入力してください。');
          return;
        }
        await transcriptionApi.updateSegmentEdits(id, {
          edited_segments: draftSegments.map(({ start, end, text, speaker }) => ({
            start, end, text, speaker: speaker || null,
          })),
        });
      } else {
        const { speakerOverrides, textOverrides } = buildSegmentOverrides();
        await transcriptionApi.updateSegmentEdits(id, {
          segment_speaker_overrides: speakerOverrides,
          segment_text_overrides: textOverrides,
        });
      }
      // サーバを真実として再取得（失敗時は表示が変わらず不整合を残さない）
      await refetch();
      stop();
      setIsEditingSegments(false);
    } catch (err) {
      console.error('Failed to update segment edits:', err);
      // 409: 別のタブ等で分割済み。再試行では直らないため再読み込みを案内する
      setSaveSegmentsError(
        err instanceof AxiosError && err.response?.status === 409
          ? 'この書き起こしは別の画面で分割されています。ページを再読み込みしてから編集してください。'
          : 'セグメントの修正の保存に失敗しました。もう一度お試しください。',
      );
    } finally {
      setIsSavingSegments(false);
    }
  };

  // ダウンロード失敗は画面に出さないと「押したのに無反応」に見えるため、メッセージを保持して表示する
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (format: DownloadFormat) => {
    if (!id) return;

    setDownloadError(null);
    try {
      const { blob, filename } = await transcriptionApi.download(id, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // 保存名はサーバの Content-Disposition（＝元の音声ファイル名）に従う。
      // blob URL ではヘッダーが効かないため download 属性に明示する必要がある。
      // ヘッダーを読めなかったときだけ、従来どおり ID ベースの名前へフォールバックする。
      a.download = filename ?? `transcription_${id}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download:', error);
      setDownloadError('ダウンロードに失敗しました。もう一度お試しください。');
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (isError || !transcription) {
    // 404（存在しない/削除済み）は従来どおり「見つかりません」、それ以外（通信障害/5xx）は再試行導線つきで表示
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    const notFound = status === 404 || (!isError && !transcription);
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center px-4">
          {notFound ? (
            <div className="text-danger-fg text-xl">書き起こしが見つかりません</div>
          ) : (
            <ErrorState
              message="書き起こしの読み込みに失敗しました"
              onRetry={() => refetch()}
              className="w-full max-w-md"
            />
          )}
        </div>
      </Layout>
    );
  }

  // 処理中・保留中の場合はプログレス画面を表示
  const isProcessing = transcription.status === 'pending' || transcription.status === 'processing';

  return (
    <Layout>
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* 処理中の場合はプログレス画面を表示 */}
        {isProcessing ? (
          <TranscriptionProgress transcription={transcription} />
        ) : (
          <>
            {/* プライバシー保護のための自動削除警告 */}
            {transcription.status === 'completed' && transcription.time_until_deletion && (
              <div role="status" className="bg-warning-bg border-l-4 border-warning p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-warning" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-warning-fg">
                      <strong className="font-bold">プライバシー保護のため、このデータは{transcription.time_until_deletion}で自動削除されます。</strong>
                      <br />
                      必要な場合は今すぐダウンロードしてください。
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 部分結果の警告 */}
            {transcription.status === 'partial' && (
              <div role="alert" className="bg-warning-bg border-l-4 border-warning p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-warning" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-warning-fg">部分的な結果です</h3>
                    <p className="text-sm text-warning-fg mt-1">
                      {transcription.error_message || '処理中にエラーが発生したため、途中までの結果を表示しています。'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* エラー表示 */}
            {transcription.status === 'failed' && (
              <div role="alert" className="bg-danger-bg border-l-4 border-danger p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-danger" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-danger-fg">処理に失敗しました</h3>
                    <p className="text-sm text-danger-fg mt-1">
                      {transcription.error_message || '不明なエラーが発生しました。もう一度お試しください。'}
                    </p>
                    {/* 再試行導線: 失敗時はアップロード画面へ誘導し、ユーザーが自力で遷移する手間を省く */}
                    <Link to="/upload" className={linkButtonClass('primary', 'mt-3', 'sm')}>
                      再アップロード
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <Card className="mb-6">
              {/* 見出しはユーザーが付けたタイトル（未設定ならファイル名）。タイトル設定時のみ、
                  元ファイル名を小さく併記して「どの音声か」を辿れるようにする。 */}
              {isEditingTitle ? (
                <div className="mb-4">
                  <label htmlFor="transcription-title" className="block text-sm font-medium text-gray-700 mb-2">
                    タイトル
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id="transcription-title"
                      type="text"
                      autoFocus
                      /* UX 上の目安。長さの最終的な真実はサーバ（MAX_TITLE_LENGTH=100） */
                      maxLength={100}
                      value={draftTitle}
                      placeholder={transcription.audio_filename}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveTitle();
                        if (e.key === 'Escape') cancelEditingTitle();
                      }}
                      disabled={isSavingTitle}
                      className="flex-1 min-w-[16rem] px-3 py-2 border border-gray-300 rounded-field focus:outline-none focus:ring-2 focus:ring-primary"
                      aria-label="書き起こしのタイトル"
                    />
                    <Button size="sm" onClick={saveTitle} disabled={isSavingTitle}>
                      {isSavingTitle ? '保存中...' : '保存'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={cancelEditingTitle} disabled={isSavingTitle}>
                      キャンセル
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    空欄にすると、元のファイル名（{transcription.audio_filename}）に戻ります。
                  </p>
                  {saveTitleError && (
                    <p className="mt-2 text-sm text-danger-fg" role="alert">{saveTitleError}</p>
                  )}
                </div>
              ) : (
                <div className="mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold">{transcriptionTitle(transcription)}</h1>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={startEditingTitle}
                      aria-label="タイトルを編集"
                    >
                      ✎ 名前を変更
                    </Button>
                  </div>
                  {transcription.title && (
                    <p className="text-sm text-gray-500 mt-1">{transcription.audio_filename}</p>
                  )}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div>
                  <div className="text-sm text-gray-600">ステータス</div>
                  <div className="mt-1">
                    {(['completed', 'partial', 'failed', 'cancelled'] as const).includes(
                      transcription.status as 'completed' | 'partial' | 'failed' | 'cancelled',
                    ) && (
                      <Badge variant={statusToBadgeVariant(transcription.status)}>
                        {statusToLabel(transcription.status)}
                      </Badge>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">音声長</div>
                  <div className="font-bold">
                    {transcription.audio_duration ? `${(transcription.audio_duration / 60).toFixed(1)} 分` : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">処理時間</div>
                  <div className="font-bold">
                    {formatDuration(transcription.processing_time ?? null)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">作成日時</div>
                  <div className="font-bold">
                    {new Date(transcription.created_at).toLocaleString('ja-JP')}
                  </div>
                </div>
                {transcription.will_be_deleted_at && (
                  <div>
                    <div className="text-sm text-gray-600">削除予定日時</div>
                    <div className="font-bold text-red-600">
                      {new Date(transcription.will_be_deleted_at).toLocaleString('ja-JP')}
                    </div>
                  </div>
                )}
              </div>

              {(transcription.status === 'completed' || transcription.status === 'partial') && (
                DOWNLOADS_ENABLED ? (
                  <div>
                    <DownloadMenu onSelect={handleDownload} />
                    {downloadError && (
                      <p className="mt-2 text-sm text-danger-fg" role="alert">
                        {downloadError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-text-sub bg-background rounded-field px-4 py-3">
                    テスト環境のため、ダウンロードは無効です。
                  </div>
                )
              )}
            </Card>

            {transcription.segments && transcription.segments.length > 0 && (() => {
              const speakerMap = getSpeakerDisplayMap(transcription.segments, transcription.speaker_names);
              const detectedSpeakers = listDetectedSpeakers(transcription.segments, transcription.speaker_names);
              // 全文コピー用のプレーンテキスト（画面表示と同じ実効値＝付け替え話者・編集テキストを反映）。
              // 「話者名: 発言」形式。話者未検出のセグメントは発言のみ。
              const effectiveSegments = getEffectiveSegments(transcription);
              const plainText = effectiveSegments
                .map((segment) => {
                  const name = segment.speaker ? speakerMap.get(segment.speaker)?.displayName : undefined;
                  return name ? `${name}: ${segment.text}` : segment.text;
                })
                .join('\n');
              const editing = isEditingSegments && canEditSegments;
              // 一覧の行: 編集中はドラフト、表示中は実効値（分割後の配列 / override 適用済み）。id は key と再生キー
              const rows: DraftSegment[] = editing
                ? draftSegments
                : effectiveSegments.map((seg, i) => ({ ...seg, id: i }));
              return (
                <Card>
                  <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                    <h2 className="text-xl font-bold">書き起こし結果</h2>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* 全文コピー: 編集中は実効値と表示が食い違うため出さない（全プランで利用可） */}
                      {!isEditingSegments && !isEditingNames && (
                        <Button size="sm" variant="secondary" onClick={() => copyTranscript(plainText)}>
                          {copied ? '✓ コピーしました！' : '📋 全文コピー'}
                        </Button>
                      )}
                      {/* セグメント手動修正（Unit 007・unlimited のみ）。話者名編集中は出さない */}
                      {canEditSegments && !isEditingNames && (
                        isEditingSegments ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {saveSegmentsError && (
                              <span className="text-sm text-danger-fg" role="alert">{saveSegmentsError}</span>
                            )}
                            <Button size="sm" onClick={saveSegmentEdits} disabled={isSavingSegments}>
                              {isSavingSegments ? '保存中...' : '編集を保存'}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={cancelEditingSegments} disabled={isSavingSegments}>
                              キャンセル
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="secondary" onClick={startEditingSegments}>
                            ✎ セグメントを編集
                          </Button>
                        )
                      )}
                    </div>
                  </div>

                  {isEditingSegments && (
                    <div role="status" className="mb-3 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                      各セグメントの話者（検出済みから選択）とテキストを修正し、「編集を保存」で反映します。「✂ ここで分割」でカーソル位置から2つに分けられます。音声を聞きながら修正できます。
                    </div>
                  )}

                  {/* 検出話者一覧（凡例）。話者なし（free/lite・分離失敗）の場合は非表示 */}
                  {detectedSpeakers.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap items-center gap-2" aria-label="検出された話者">
                        <span className="text-sm text-gray-600">検出された話者:</span>
                        {detectedSpeakers.map((sp) => (
                          <span
                            key={sp.rawLabel}
                            className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full"
                            style={{ backgroundColor: sp.color.bg, color: sp.color.text, border: `1px solid ${sp.color.border}` }}
                          >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sp.color.border }} />
                            {sp.displayName}
                          </span>
                        ))}
                        {/* standard / unlimited のみ話者名の編集が可能（セグメント編集中は出さない） */}
                        {canRename && !isEditingNames && !isEditingSegments && (
                          <Button size="sm" variant="secondary" onClick={() => startEditingNames(detectedSpeakers)}>
                            ✎ 名前を編集
                          </Button>
                        )}
                      </div>

                      {/* 編集モード: 各話者に入力欄→一括保存 */}
                      {isEditingNames && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                          {detectedSpeakers.map((sp, idx) => (
                            <div key={sp.rawLabel} className="flex items-center gap-2">
                              <span
                                className="inline-flex items-center text-sm px-2.5 py-1 rounded-full shrink-0"
                                style={{ backgroundColor: sp.color.bg, color: sp.color.text, border: `1px solid ${sp.color.border}` }}
                              >
                                話者{idx + 1}
                              </span>
                              <input
                                type="text"
                                /* UX 上の目安。長さの最終的な真実はサーバ（MAX_SPEAKER_NAME_LENGTH=50・コードポイント数）。
                                   絵文字等のサロゲートペアで maxLength(UTF-16 単位)と差が出てもサーバが切り詰める */
                                maxLength={50}
                                value={draftNames[sp.rawLabel] ?? ''}
                                placeholder={`話者${idx + 1}（未入力で既定名）`}
                                onChange={(e) =>
                                  setDraftNames((prev) => ({ ...prev, [sp.rawLabel]: e.target.value }))
                                }
                                disabled={isSavingNames}
                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-field text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                aria-label={`話者${idx + 1}の名前`}
                              />
                            </div>
                          ))}
                          {saveNamesError && (
                            <p className="text-sm text-danger-fg" role="alert">{saveNamesError}</p>
                          )}
                          <div className="flex gap-2 pt-1">
                            <Button size="sm" onClick={saveSpeakerNames} disabled={isSavingNames}>
                              {isSavingNames ? '保存中...' : '保存'}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={cancelEditingNames} disabled={isSavingNames}>
                              キャンセル
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* セグメント再生の案内（Unit 006 / 008）。Option B: 再生可否は保持状態(audio_available)に従う（プラン非依存）。
                      表示条件は「現 unlimited（期限切れ等の説明文を従来どおり提示＝後退回帰防止）または 保持音声あり（降格後も保持期限内は再生可）」。
                      保持対象外プランには false 分岐の誤った削除文言を出さない。 */}
                  {(canPlaySegments || transcription.audio_available) && (
                    transcription.audio_available ? (
                      <div role="status" className="mb-3 text-sm text-gray-600">
                        🔊 各セグメントの「▶ 再生」で音声を聞けます。
                        {transcription.audio_expires_at && (
                          <span>
                            {' '}再生可能期限:{' '}
                            <strong>{new Date(transcription.audio_expires_at).toLocaleString('ja-JP')}</strong>
                          </span>
                        )}
                      </div>
                    ) : (
                      // この false 分岐は「現 unlimited（canPlaySegments=true）× audio_available=false」のときのみ到達する。
                      // audio_available の false 要因は partial（保持されない）と期限切れ（削除済み）の2つを前提とし、文言を出し分ける。
                      <div role="status" className="mb-3 text-sm text-gray-500">
                        {transcription.status === 'partial'
                          ? '部分結果のため音声は保持されません（再生できません）。'
                          : '再生可能期限を過ぎたため、音声は削除されました（再生できません）。'}
                      </div>
                    )
                  )}

                  <div className="bg-gray-50 p-4 rounded-lg max-h-[600px] overflow-y-auto space-y-4">
                    {rows.map((segment, index) => {
                      const key = String(segment.id);
                      const sp = segment.speaker ? speakerMap.get(segment.speaker) : undefined;
                      return (
                        <div
                          key={key}
                          className="border-l-4 border-primary pl-4 py-2"
                          style={sp ? { borderColor: sp.color.border } : undefined}
                        >
                          <div className="text-xs text-gray-500 font-mono mb-1 flex items-center gap-2 flex-wrap">
                            <span>{formatTimestamp(segment.start)} - {formatTimestamp(segment.end)}</span>
                            {/* 話者: 編集モードはドロップダウン（検出済みから選択）、表示モードはバッジ */}
                            {editing ? (
                              detectedSpeakers.length > 0 && (
                                <select
                                  value={segment.speaker ?? ''}
                                  onChange={(e) => updateDraft(segment.id, { speaker: e.target.value })}
                                  disabled={isSavingSegments}
                                  className="font-sans text-[12px] px-1.5 py-0.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                                  aria-label={`セグメント${index + 1}の話者`}
                                >
                                  {!segment.speaker && <option value="">（話者なし）</option>}
                                  {detectedSpeakers.map((opt) => (
                                    <option key={opt.rawLabel} value={opt.rawLabel}>
                                      {opt.displayName}
                                    </option>
                                  ))}
                                </select>
                              )
                            ) : (
                              sp && (
                                <span
                                  className="font-sans px-1.5 py-0.5 rounded text-[11px] font-semibold"
                                  style={{ backgroundColor: sp.color.bg, color: sp.color.text }}
                                >
                                  {sp.displayName}
                                </span>
                              )
                            )}
                            {/* セグメント音声再生（Unit 006 / 008）。Option B: 保持音声があり再生可能(audio_available)なときのみ表示。
                                プラン非依存（降格後も保持期限内は再生可）。表示条件＝活性条件のため disabled は不要（到達不能分岐を残さない）。
                                再生不可（期限切れ・未保持）の理由は上部の案内ブロックが提示する。編集中も再生可（分割後の時刻で再生）。 */}
                            {transcription.audio_available && (
                              <button
                                type="button"
                                onClick={() =>
                                  playingKey === key
                                    ? stop()
                                    : play(segment.start, segment.end, key)
                                }
                                className="font-sans inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-gray-300 hover:bg-gray-100"
                                aria-label={playingKey === key ? 'この区間の再生を停止' : 'この区間を再生'}
                              >
                                {playingKey === key ? '■ 停止' : '▶ 再生'}
                              </button>
                            )}
                            {/* 分割（v3.8.0）: textarea のカーソル位置で2つに分ける */}
                            {editing && (
                              <button
                                type="button"
                                onClick={() => splitDraft(segment.id)}
                                disabled={isSavingSegments}
                                className="font-sans inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-gray-300 hover:bg-gray-100"
                                aria-label={`セグメント${index + 1}をカーソル位置で分割`}
                              >
                                ✂ ここで分割
                              </button>
                            )}
                          </div>
                          {/* テキスト: 編集モードは textarea、表示モードは編集後テキスト */}
                          {editing ? (
                            <textarea
                              ref={(el) => {
                                // 初回マウント時だけ高さを合わせる（毎レンダーで全行を再計測しない）
                                if (!el || textareaRefs.current[segment.id] === el) return;
                                textareaRefs.current[segment.id] = el;
                                autoGrow(el);
                              }}
                              value={segment.text}
                              onChange={(e) => {
                                autoGrow(e.target);
                                updateDraft(segment.id, { text: e.target.value });
                              }}
                              disabled={isSavingSegments}
                              maxLength={5000}
                              rows={2}
                              className="w-full resize-none overflow-hidden px-3 py-1.5 border border-gray-300 rounded-field text-sm text-gray-800 leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
                              aria-label={`セグメント${index + 1}のテキスト`}
                            />
                          ) : (
                            <div className="text-gray-800 leading-relaxed">
                              {segment.text}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })()}
          </>
        )}
      </main>

      {/* 未保存の変更を捨てる前の確認。丁寧に直した内容が警告なしに消えるのを防ぐ */}
      <ConfirmDialog
        open={pendingDiscard !== null}
        title="保存されていない変更があります"
        message="編集内容は保存されていません。破棄してもよろしいですか？"
        confirmLabel="破棄する"
        cancelLabel="編集に戻る"
        variant="danger"
        onConfirm={confirmDiscard}
        onClose={() => setPendingDiscard(null)}
      />
    </Layout>
  );
}
